// Universal ⌘K command palette.
// Fetches RBAC-filtered results from /api/search (backend guarantees scope).
// Keyboard: ⌘K / Ctrl+K to open anywhere; Arrow/Enter to navigate; Esc closes.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
    CommandDialog, CommandInput, CommandList, CommandEmpty,
    CommandGroup, CommandItem,
} from "@/components/ui/command";
import { DialogTitle } from "@/components/ui/dialog";
import {
    LayoutDashboard, Database, FolderKanban, Layers, Plug,
    GalleryHorizontalEnd, Settings, History, FileText,
} from "lucide-react";

// Kind → {label, icon, group order}. Unknown kinds render with a File icon.
const KIND_META = {
    recent:      { label: "Recent",          icon: History,            order: 0 },
    page:        { label: "Jump to",         icon: Settings,           order: 1 },
    dashboard:   { label: "Dashboards",      icon: LayoutDashboard,    order: 2 },
    dataset:     { label: "Datasets",        icon: Database,           order: 3 },
    workspace:   { label: "Workspaces",      icon: FolderKanban,       order: 4 },
    data_model:  { label: "Data models",     icon: Layers,             order: 5 },
    connector:   { label: "Connectors",      icon: Plug,               order: 6 },
    gallery_item:{ label: "Gallery",         icon: GalleryHorizontalEnd, order: 7 },
};

export function useCommandPalette() {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        const onKey = (e) => {
            if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((o) => !o);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);
    return { open, setOpen };
}

export default function CommandPalette({ open, onOpenChange }) {
    const navigate = useNavigate();
    const [q, setQ] = useState("");
    const [groups, setGroups] = useState({});
    const [loading, setLoading] = useState(false);
    const debounceRef = useRef(null);

    const fetchResults = useCallback(async (query) => {
        setLoading(true);
        try {
            const r = await api.get("/search", { params: { q: query, limit: 6 } });
            setGroups(r.data?.groups || {});
        } catch {
            setGroups({});
        } finally {
            setLoading(false);
        }
    }, []);

    // Debounce 180ms
    useEffect(() => {
        if (!open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchResults(q), 180);
        return () => clearTimeout(debounceRef.current);
    }, [q, open, fetchResults]);

    // Reset query when closing; pre-fetch empty state when opening
    useEffect(() => {
        if (open) {
            setQ("");
            fetchResults("");
        }
    }, [open, fetchResults]);

    const go = (route) => {
        onOpenChange(false);
        if (route) navigate(route);
    };

    // Stable ordered groups
    const groupEntries = Object.entries(groups)
        .sort(([a], [b]) => (KIND_META[a]?.order ?? 99) - (KIND_META[b]?.order ?? 99));

    return (
        <CommandDialog open={open} onOpenChange={onOpenChange}>
            <DialogTitle className="sr-only">Universal search</DialogTitle>
            <CommandInput
                placeholder="Search dashboards, datasets, data models, settings…"
                value={q}
                onValueChange={setQ}
                data-testid="cmdk-input"
            />
            <CommandList data-testid="cmdk-list">
                {loading && groupEntries.length === 0 && (
                    <div className="py-6 text-center text-xs text-muted-foreground" data-testid="cmdk-loading">
                        Searching…
                    </div>
                )}
                {!loading && groupEntries.length === 0 && (
                    <CommandEmpty data-testid="cmdk-empty">
                        No matches. Try &quot;dashboard&quot;, &quot;campaign&quot;, or a settings tab name.
                    </CommandEmpty>
                )}
                {groupEntries.map(([kind, items]) => {
                    const meta = KIND_META[kind] || { label: kind, icon: FileText };
                    const Icon = meta.icon;
                    return (
                        <CommandGroup key={kind} heading={meta.label} data-testid={`cmdk-group-${kind}`}>
                            {items.map((it) => (
                                <CommandItem
                                    key={`${kind}:${it.id}`}
                                    value={`${kind} ${it.title} ${it.subtitle || ""}`}
                                    onSelect={() => go(it.route)}
                                    className="group dark:data-[selected=true]:!bg-white dark:data-[selected=true]:!text-black"
                                    data-testid={`cmdk-item-${kind}-${it.id}`}
                                >
                                    <Icon className="h-4 w-4 text-muted-foreground shrink-0 dark:group-data-[selected=true]:!text-black" />
                                    <div className="flex flex-col min-w-0 flex-1">
                                        <span className="truncate text-sm dark:group-data-[selected=true]:!text-black">{it.title}</span>
                                        {it.subtitle && (
                                            <span className="truncate text-[11px] font-mono text-muted-foreground dark:group-data-[selected=true]:!text-black">
                                                {it.subtitle}
                                            </span>
                                        )}
                                    </div>
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    );
                })}
            </CommandList>
            <div className="flex items-center justify-between px-3 py-2 border-t border-border text-[10px] font-mono text-muted-foreground">
                <span>
                    <kbd className="bg-secondary px-1 rounded-sm">↑↓</kbd> navigate ·{" "}
                    <kbd className="bg-secondary px-1 rounded-sm">↵</kbd> open ·{" "}
                    <kbd className="bg-secondary px-1 rounded-sm">esc</kbd> close
                </span>
                <span>Scoped by your RBAC permissions</span>
            </div>
        </CommandDialog>
    );
}
